import { useId } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Label } from "@volca/ui/components/ui/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@volca/ui/components/ui/radio-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  TimeRangePicker,
  type TimeRange,
} from "@volca/ui/components/ui/time-range-picker";
import { REPORT_PERIOD_PRESETS } from "@/profitability/constants";
import type { OverviewPeriod } from "@/profitability/overview/types";
import type { ReportDates } from "@/profitability/types";
import { formatReportPeriod } from "@/profitability/utils";

/**
 * Which period the files are for: the next one, an earlier one that has
 * not run, one that ran, or other dates.
 */
export type PeriodChoice =
  | { kind: "next" }
  | { kind: "earlier"; startDate: string }
  | { kind: "period"; runId: string }
  | { kind: "custom" };

const EARLIER = "earlier:";

export const choiceValue = (choice: PeriodChoice) => {
  switch (choice.kind) {
    case "period":
      return choice.runId;
    case "earlier":
      return `${EARLIER}${choice.startDate}`;
    case "next":
    case "custom":
      return choice.kind;
    default: {
      const unknown: never = choice;
      return unknown;
    }
  }
};

const choiceOf = (value: string): PeriodChoice =>
  value === "next" || value === "custom"
    ? { kind: value }
    : value.startsWith(EARLIER)
      ? { kind: "earlier", startDate: value.slice(EARLIER.length) }
      : { kind: "period", runId: value };

const Option = ({
  value,
  title,
  note,
}: {
  value: string;
  title: string;
  note: string;
}) => {
  const id = useId();
  return (
    <Label
      htmlFor={id}
      className="flex cursor-pointer items-start gap-3 rounded-lg border border-line px-4 py-3 has-data-checked:border-focus has-data-checked:bg-surface-sunken"
    >
      <RadioGroupItem id={id} value={value} className="mt-0.5" />
      <Stack gap={0.5} align="start">
        <span className="text-sm font-medium text-ink tabular-nums">
          {title}
        </span>
        <span className="text-sm font-normal text-subtle">{note}</span>
      </Stack>
    </Label>
  );
};

/**
 * Which period the files are for. Uploading isn't only the next period:
 * files that fill in or correct a period that already ran update it, so the
 * periods that ran are choices beside the next one. Suggested, never
 * assumed: the upload date says nothing about which week an export holds.
 */
export const RunPeriodChoice = ({
  next,
  earlier = [],
  periods,
  choice,
  onChoiceChange,
  custom,
  onCustomChange,
}: {
  /** The next period by the calendar, or null when there's none to suggest. */
  next: ReportDates | null;
  /** The periods before it that have not run, newest first (`earlierPeriods`). */
  earlier?: ReportDates[];
  /** The periods that ran, newest first. */
  periods: OverviewPeriod[];
  choice: PeriodChoice;
  onChoiceChange: (choice: PeriodChoice) => void;
  custom: TimeRange | null;
  onCustomChange: (value: TimeRange | null) => void;
}) => (
  <Card className="flex min-w-0 flex-col gap-4 border-line p-6 shadow-none">
    <Stack gap={1}>
      <Text variant="title" render={<h2 />}>
        Which period are these files for?
      </Text>
      <Text tone="muted">
        A new period, or one that already ran: files for a period that ran fill
        in or correct it.
      </Text>
    </Stack>
    <RadioGroup
      aria-label="Period"
      value={choiceValue(choice)}
      onValueChange={(value) => onChoiceChange(choiceOf(String(value)))}
      className="grid gap-2"
    >
      {next && (
        <Option
          value="next"
          title={formatReportPeriod(next)}
          note="New period"
        />
      )}
      {earlier.map((period) => (
        <Option
          key={period.start_date}
          value={`${EARLIER}${period.start_date}`}
          title={formatReportPeriod(period)}
          note="Earlier period, not run yet"
        />
      ))}
      {periods.map((period) => (
        <Option
          key={period.run_id}
          value={period.run_id}
          title={formatReportPeriod(period)}
          note={
            period.reviewed_at
              ? "Updates this period. It was reviewed, so the update waits until you use it."
              : "Updates this period. Its files are filled in; replace the ones that changed."
          }
        />
      ))}
      <Option
        value="custom"
        title="Other dates"
        note="Any dates, or none for files that aren't for one period."
      />
    </RadioGroup>
    {choice.kind === "custom" && (
      <TimeRangePicker
        placeholder="No period"
        presets={REPORT_PERIOD_PRESETS}
        value={custom}
        onChange={onCustomChange}
        allowTextEntry
      />
    )}
  </Card>
);
